import React, { useEffect, useState } from 'react';
import { X, Loader, MessageCircle, Camera, HardHat, CloudRain, Truck, CalendarDays, CalendarRange, Megaphone, Activity, Moon } from 'lucide-react';
import { alertasApi, errorMsg } from '../../camera/components/alertas/alertasObra.api';

// Preferencias de notificación de la empresa (campanita). Se guardan por número de WhatsApp en
// alertConfigs (el mismo documento que lee el bot) y valen para todas las cámaras del cliente.

type Categoria = 'camara' | 'actividad' | 'seguridad' | 'clima' | 'entregas' | 'reporteDiario' | 'resumenSemanal' | 'avisosCuenta';
type Frecuencia = 'momento' | 'diaria' | 'semanal';

interface Prefs {
  categorias: Record<Categoria, boolean>;
  frecuencia: Frecuencia;
  horaResumen: string;
  silencio: { activo: boolean; desde: string; hasta: string; finDeSemana: boolean };
}

interface Numero {
  phone: string;
  activo: boolean;
  notifWsp: boolean;
  obras: { feedId: string; name: string }[];
  entregasHabilitado: boolean;
  preferencias: Prefs;
}

const CATEGORIAS: { key: Categoria; titulo: string; detalle: string; Icon: React.ElementType }[] = [
  { key: 'camara', titulo: 'Cámara sin señal', detalle: 'Si una cámara deja de mandar fotos', Icon: Camera },
  { key: 'actividad', titulo: 'Actividad de obra', detalle: 'Obra parada, arranque tarde o poca gente', Icon: Activity },
  { key: 'seguridad', titulo: 'Seguridad', detalle: 'Personas sin casco', Icon: HardHat },
  { key: 'clima', titulo: 'Clima', detalle: 'Lluvia, viento, tormenta y día para hormigonar', Icon: CloudRain },
  { key: 'entregas', titulo: 'Llegó material', detalle: 'Camiones y mixers en la obra', Icon: Truck },
  { key: 'reporteDiario', titulo: 'Reporte diario', detalle: 'Cómo fue la jornada', Icon: CalendarDays },
  { key: 'resumenSemanal', titulo: 'Resumen semanal', detalle: 'Los lunes a la mañana', Icon: CalendarRange },
  { key: 'avisosCuenta', titulo: 'Avisos de tu cuenta', detalle: 'Pendientes y novedades de Lisual', Icon: Megaphone },
];

const FRECUENCIAS: { key: Frecuencia; titulo: string; detalle: string }[] = [
  { key: 'momento', titulo: 'Al momento', detalle: 'Apenas pasa' },
  { key: 'diaria', titulo: '1 vez por día', detalle: 'Todo junto en un mensaje' },
  { key: 'semanal', titulo: '1 vez por semana', detalle: 'Solo un resumen los lunes' },
];

// 5491123456789 -> +54 9 11 2345-6789 (solo para mostrar)
export const formatoTelefono = (p: string) => {
  const m = /^54(9?)(11|\d{3,4})(\d{3,4})(\d{4})$/.exec(p);
  return m ? `+54 ${m[1] ? '9 ' : ''}${m[2]} ${m[3]}-${m[4]}` : `+${p}`;
};

const Switch: React.FC<{ on: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string }> = ({ on, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${on ? 'bg-green-600' : 'bg-gray-300'}`}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

interface Props {
  customerId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const NotificationPreferences: React.FC<Props> = ({ customerId, isOpen, onClose, onSaved }) => {
  const [numeros, setNumeros] = useState<Numero[] | null>(null);
  const [error, setError] = useState('');
  const [sel, setSel] = useState('');
  const [draft, setDraft] = useState<Prefs | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!isOpen || !customerId) return;
    setNumeros(null); setError(''); setOk(false);
    alertasApi.preferencias(customerId)
      .then((r: { numeros: Numero[] }) => {
        setNumeros(r.numeros || []);
        const first = r.numeros?.[0];
        setSel(first?.phone || '');
        setDraft(first ? structuredClone(first.preferencias) : null);
      })
      .catch((e: any) => setError(errorMsg(e)));
  }, [isOpen, customerId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const actual = numeros?.find((n) => n.phone === sel);
  const cambiado = !!(actual && draft && JSON.stringify(actual.preferencias) !== JSON.stringify({ ...actual.preferencias, ...draft }));

  const elegir = (phone: string) => {
    const n = numeros?.find((x) => x.phone === phone);
    setSel(phone); setOk(false); setError('');
    setDraft(n ? structuredClone(n.preferencias) : null);
  };
  const setCat = (k: Categoria, v: boolean) => { setOk(false); setDraft((d) => d && { ...d, categorias: { ...d.categorias, [k]: v } }); };
  const setSil = (patch: Partial<Prefs['silencio']>) => { setOk(false); setDraft((d) => d && { ...d, silencio: { ...d.silencio, ...patch } }); };

  const guardar = async () => {
    if (!customerId || !actual || !draft) return;
    setGuardando(true); setError('');
    try {
      const n: Numero = await alertasApi.guardarPreferencias(customerId, actual.phone, draft);
      setNumeros((ns) => (ns || []).map((x) => (x.phone === n.phone ? n : x)));
      setDraft(structuredClone(n.preferencias));
      setOk(true);
      onSaved?.();
    } catch (e: any) {
      setError(errorMsg(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Preferencias de notificación"
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[92dvh] w-full flex-col rounded-t-2xl bg-white shadow-2xl sm:max-w-lg sm:rounded-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <p className="text-lg font-semibold text-gray-900">Preferencias de notificación</p>
            <p className="text-sm text-gray-500">Qué te llega por WhatsApp, para todas tus cámaras</p>
          </div>
          <button type="button" aria-label="Cerrar" onClick={onClose} className="p-2 -mr-2 -mt-1 text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!numeros && !error && (
            <div className="flex justify-center py-10"><Loader size={28} className="animate-spin text-blue-500" /></div>
          )}

          {!numeros && error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          {numeros && numeros.length === 0 && (
            <div className="py-8 text-center">
              <MessageCircle size={32} className="mx-auto text-green-600" />
              <p className="mt-3 font-semibold text-gray-900">Todavía no hay números de WhatsApp</p>
              <p className="mt-1 text-sm text-gray-600">Sumá uno desde una cámara, en <b>Inteligencia › Alertas</b>. Después lo configurás acá.</p>
            </div>
          )}

          {actual && draft && (
            <div className="space-y-6">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Número</p>
                {numeros!.length > 1 ? (
                  <select
                    value={sel}
                    onChange={(e) => elegir(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900"
                  >
                    {numeros!.map((n) => <option key={n.phone} value={n.phone}>{formatoTelefono(n.phone)}</option>)}
                  </select>
                ) : (
                  <p className="flex items-center gap-2 font-medium text-gray-900"><MessageCircle size={18} className="text-green-600" /> {formatoTelefono(actual.phone)}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {actual.obras.length === 1 ? `Recibe avisos de ${actual.obras[0].name}` : `Recibe avisos de ${actual.obras.length} cámaras`}
                  {!actual.activo && ' · pausado'}
                </p>
              </div>

              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">¿Qué te avisamos?</p>
                <ul className="divide-y divide-gray-100">
                  {CATEGORIAS.map(({ key, titulo, detalle, Icon }) => {
                    const bloqueada = key === 'entregas' && !actual.entregasHabilitado && !draft.categorias.entregas;
                    return (
                      <li key={key} className="flex items-center gap-3 py-3">
                        <Icon size={20} className="shrink-0 text-gray-500" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-gray-900">{titulo}</span>
                          <span className="block text-xs text-gray-500">{bloqueada ? 'Viene con IA de obra' : detalle}</span>
                        </span>
                        <Switch label={titulo} on={draft.categorias[key]} disabled={bloqueada} onChange={(v) => setCat(key, v)} />
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">¿Cada cuánto?</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {FRECUENCIAS.map((f) => {
                    const on = draft.frecuencia === f.key;
                    return (
                      <button
                        key={f.key}
                        type="button"
                        aria-pressed={on}
                        onClick={() => { setOk(false); setDraft({ ...draft, frecuencia: f.key }); }}
                        className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${on ? 'border-green-600 bg-green-50 ring-1 ring-green-600' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <span className="block text-sm font-semibold text-gray-900">{f.titulo}</span>
                        <span className="block text-xs text-gray-500">{f.detalle}</span>
                      </button>
                    );
                  })}
                </div>
                {draft.frecuencia !== 'momento' && (
                  <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
                    {draft.frecuencia === 'diaria' ? 'Mandalo a las' : 'Los lunes a las'}
                    <input
                      type="time"
                      value={draft.horaResumen}
                      onChange={(e) => { setOk(false); setDraft({ ...draft, horaResumen: e.target.value }); }}
                      className="rounded-lg border border-gray-300 px-2 py-1.5"
                    />
                  </label>
                )}
                {draft.frecuencia !== 'momento' && (
                  <p className="mt-2 text-xs text-gray-500">Si una cámara se queda sin señal o hay tormenta, te avisamos igual al momento.</p>
                )}
              </div>

              <div>
                <div className="flex items-center gap-3">
                  <Moon size={20} className="shrink-0 text-gray-500" />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-gray-900">Horario de silencio</span>
                    <span className="block text-xs text-gray-500">No te escribimos en ese horario</span>
                  </span>
                  <Switch label="Horario de silencio" on={draft.silencio.activo} onChange={(v) => setSil({ activo: v })} />
                </div>
                {draft.silencio.activo && (
                  <div className="mt-3 space-y-3 rounded-xl bg-gray-50 p-3">
                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                      De
                      <input type="time" aria-label="Silencio desde" value={draft.silencio.desde} onChange={(e) => setSil({ desde: e.target.value })} className="rounded-lg border border-gray-300 px-2 py-1.5" />
                      a
                      <input type="time" aria-label="Silencio hasta" value={draft.silencio.hasta} onChange={(e) => setSil({ hasta: e.target.value })} className="rounded-lg border border-gray-300 px-2 py-1.5" />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={draft.silencio.finDeSemana} onChange={(e) => setSil({ finDeSemana: e.target.checked })} className="h-4 w-4 rounded border-gray-300 accent-green-600" />
                      Sábados y domingos, todo el día
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {actual && draft && (
          <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {error && <p className="mr-auto text-sm text-red-600">{error}</p>}
            {ok && !cambiado && !error && <p className="mr-auto text-sm text-green-700">Listo, guardado</p>}
            <button type="button" onClick={onClose} className="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100">
              Cerrar
            </button>
            <button
              type="button"
              disabled={!cambiado || guardando}
              onClick={guardar}
              className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
            >
              {guardando && <Loader size={16} className="animate-spin" />}
              Guardar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPreferences;
